import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useRoom } from '../net/useRoom.js';
import { LiveSocketDriver } from '../driver/index.js';
import { useNarration } from '../audio/useNarration.js';
import { useNightAmbient } from '../audio/useNightAmbient.js';
import { StageView } from '../stage/index.js';

/**
 * Stage (`/stage/:code`) — the pure SPECTATOR / broadcast surface.
 *
 * A TV, a projector, or a Discord Activity viewer watching a live room by code.
 * It is a read-only window onto the PUBLIC game: it attaches a LiveSocketDriver
 * as the 'stage' role, which sends WATCH { code } on every socket open (and
 * reconnects forever). The server binds it as a playerless Stage, so it receives
 * every PUBLIC_STATE + NARRATION but NEVER a PRIVATE_STATE — no secret can reach
 * a watcher. It renders the existing StageView with isHost={false}, so no
 * gameplay control ever appears.
 *
 * Audio: muted by default (a broadcast surface should not auto-blast sound). Add
 * `?audio=1` to opt in — a one-time "Click to enable sound" overlay unlocks the
 * AudioContext inside the user's tap and starts narration playback from there.
 *
 * Layout: a black 16:9 aspect-locked stage (letterboxed on any screen), so the
 * broadcast reads the same on a phone, a laptop, or a TV.
 */
export default function Stage() {
  const { code: codeParam } = useParams();
  const code = (codeParam ?? '').toUpperCase();

  // Individual selectors (never a fresh object) to avoid zustand's #185 loop.
  const publicView = useRoom((s) => s.publicView);
  const attach = useRoom((s) => s.attach);

  // Audio opt-in: `?audio=1` shows a one-time unlock overlay; otherwise silent.
  const audioEnabled = useAudioFlag();
  // Audio only actually plays once the viewer taps the unlock overlay.
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const { unlock, caption } = useNarration(audioEnabled && audioUnlocked);
  // Stage-only ambient soundscape follows the public phase, gated on the unlock.
  useNightAmbient(audioEnabled && audioUnlocked, publicView?.phase);

  // Keep a handle on the live driver so we can poll its socket for the
  // "reconnecting" glyph.
  const driverRef = useRef<LiveSocketDriver | null>(null);

  // Attach the spectator driver once on mount. role:'stage' makes the driver
  // send WATCH { code } on every open and reconnect indefinitely.
  useEffect(() => {
    if (!code) return;
    const d = new LiveSocketDriver();
    driverRef.current = d;
    attach(d, { role: 'stage', code });
    return () => {
      driverRef.current = null;
      useRoom.getState().detach();
    };
  }, [attach, code]);

  const reconnecting = useReconnecting(driverRef);
  const idle = useCursorIdle(3000);

  function handleEnableSound() {
    unlock();
    setAudioUnlocked(true);
  }

  return (
    <div
      style={{
        height: '100dvh',
        display: 'grid',
        placeItems: 'center',
        background: '#000',
        cursor: idle ? 'none' : 'auto',
        overflow: 'hidden',
      }}
    >
      {/* 16:9 aspect-locked stage — letterboxed on any viewport. */}
      <div
        style={{
          position: 'relative',
          aspectRatio: '16 / 9',
          width: 'min(100vw, calc(100dvh * 16 / 9))',
          height: 'min(100dvh, calc(100vw * 9 / 16))',
          overflow: 'hidden',
          background: '#000',
        }}
      >
        {publicView ? (
          <StageView
            publicView={publicView}
            caption={audioEnabled ? caption : null}
            onEndDiscussion={noop}
            onPlayAgain={noop}
            isHost={false}
          />
        ) : (
          <ConnectingPane code={code} />
        )}

        {/* One-time "enable sound" overlay (only when ?audio=1 and not yet unlocked). */}
        {audioEnabled && !audioUnlocked && publicView && (
          <button
            type="button"
            onClick={handleEnableSound}
            style={{
              position: 'absolute',
              inset: 0,
              display: 'grid',
              placeItems: 'center',
              background: 'rgba(0,0,0,0.55)',
              border: 'none',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-ui)',
              fontSize: 18,
              letterSpacing: '0.02em',
              cursor: 'pointer',
              zIndex: 20,
            }}
          >
            <span
              style={{
                padding: '14px 26px',
                borderRadius: 'var(--radius-pill)',
                border: '1px solid var(--btn-secondary-stroke)',
                background: 'rgba(0,0,0,0.4)',
              }}
            >
              Click to enable sound
            </span>
          </button>
        )}

        {/* Subtle reconnecting glyph on socket loss — no modal, corner only. */}
        {reconnecting && <ReconnectingGlyph />}
      </div>
    </div>
  );
}

function noop() {
  /* spectator has no gameplay controls */
}

/** True when `?audio=1` is present in the URL query. */
function useAudioFlag(): boolean {
  const [enabled] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('audio') === '1';
  });
  return enabled;
}

/**
 * Poll the live driver's socket state to drive a "reconnecting" indicator.
 * Returns true while the socket is not currently OPEN (initial connect gap or a
 * dropped-and-retrying stage socket). Debounced so a momentary blip doesn't
 * flash the glyph.
 */
function useReconnecting(driverRef: React.MutableRefObject<LiveSocketDriver | null>): boolean {
  const [reconnecting, setReconnecting] = useState(false);
  useEffect(() => {
    let downSince = 0;
    const id = setInterval(() => {
      const connected = driverRef.current?.isConnected() ?? false;
      if (connected) {
        downSince = 0;
        setReconnecting(false);
      } else {
        const now = Date.now();
        if (downSince === 0) downSince = now;
        // Only surface the glyph after ~1.2s of downtime (ignore reconnect blips).
        setReconnecting(now - downSince > 1200);
      }
    }, 500);
    return () => clearInterval(id);
  }, [driverRef]);
  return reconnecting;
}

/** Hide the cursor after `ms` of no pointer movement; show it again on move. */
function useCursorIdle(ms: number): boolean {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    const arm = () => {
      setIdle(false);
      clearTimeout(timer);
      timer = setTimeout(() => setIdle(true), ms);
    };
    arm();
    window.addEventListener('pointermove', arm);
    window.addEventListener('pointerdown', arm);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('pointermove', arm);
      window.removeEventListener('pointerdown', arm);
    };
  }, [ms]);
  return idle;
}

/** Pre-first-state pane: a quiet "waiting for the room" message on black. */
function ConnectingPane({ code }: { code: string }) {
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'var(--bg-gradient)',
        color: 'var(--text-secondary)',
        fontFamily: 'var(--font-ui)',
        textAlign: 'center',
        gap: 8,
      }}
    >
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 28, color: 'var(--text-primary)' }}>
          Ringmaster
        </div>
        <div style={{ marginTop: 6 }}>Connecting to room {code}…</div>
      </div>
    </div>
  );
}

/** A tiny corner "reconnecting" indicator — a pulsing dot + label. */
function ReconnectingGlyph() {
  return (
    <div
      style={{
        position: 'absolute',
        top: 14,
        right: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        borderRadius: 'var(--radius-pill)',
        background: 'rgba(0,0,0,0.5)',
        color: 'var(--text-secondary)',
        fontFamily: 'var(--font-ui)',
        fontSize: 12,
        letterSpacing: '0.04em',
        zIndex: 30,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: 'var(--accent-blood-end)',
          animation: 'stage-reconnect-pulse 1.1s ease-in-out infinite',
        }}
        aria-hidden
      />
      Reconnecting…
      <style>
        {`@keyframes stage-reconnect-pulse { 0%,100% { opacity: 0.35; } 50% { opacity: 1; } }`}
      </style>
    </div>
  );
}
